import get from 'lodash/get';
import { createSelector } from 'reselect';
import { getIsEventDefaultFormV2 } from 'common/entities/event/selectors';
import { APP } from '../index';
import { TICKET_RATES_2016 } from '../../compounds/TicketsTab/constants';

// Not intended for use outside of this file, if you need this value
// somewhere else, consider making a new selector.
export const USER_PROPERTY_VALUE_SHOW = 'show';
export const USER_PROPERTY_VALUE_HIDE = 'hide';
export const USER_PROPERTY_VALUE_ENABLE = 'enable';
export const USER_PROPERTY_VALUE_ON = 1;
export const USER_PROPERTY_VALUE_OFF = 0;

export const getUserProperties = (state) => get(state, [APP, 'userProperties', 'data'], {});
export const getUserTrackingData = (state) => get(state, [APP, 'user', 'data', 'trackingData'], {});

/* Guest List Access */
export const canUserAccessGuestList = createSelector(
    getUserProperties,
    (userProperties) => userProperties.splashResponsesTab === USER_PROPERTY_VALUE_SHOW,
);

/* Legacy Guest List (tickets) Access */
export const canUserAccessTicketedGuestList = createSelector(
    getUserProperties,
    (userProperties) => userProperties.splashResponsesTab === USER_PROPERTY_VALUE_SHOW,
);

/* Confirmation Touchpoint and Confirmation Dashboard Access */
export const canUserAccessConfirmations = createSelector(
    getUserProperties,
    (userProperties) => parseInt(userProperties.splashConfirmationsTab, 10) === 1,
);

/* Email Sender Access */
export const getCanUserAccessEmailSender = createSelector(
    getUserProperties,
    (userProperties) => userProperties.splashInviteTab === USER_PROPERTY_VALUE_SHOW,
);

export const getCanUserAccessScheduledEmails = createSelector(
    getUserProperties,
    (userProperties) => parseInt(userProperties.emailSchedulingAccess, 10) === 1,
);

/* Analytics Access */
export const canUserAccessAnalytics = createSelector(
    getUserProperties,
    (userProperties) => userProperties.splashAnalyticsTab === USER_PROPERTY_VALUE_SHOW,
);

/* Google Tag Manager Access */
export const canUserAccessGoogleTagManager = createSelector(
    getUserProperties,
    (userProperties) => userProperties.allowGoogleTagManagerFieldAccess === USER_PROPERTY_VALUE_ENABLE,
);

/* Themes Dashboard Access */
export const getCanUserAccessThemesDashboard = createSelector(
    getUserProperties,
    (userProperties) => parseInt(userProperties.disableThemesDashboard, 10) !== 1,
);

/* Create Theme Access */
export const canUserCreateTheme = createSelector(
    getUserProperties,
    (userProperties) => parseInt(userProperties.disableCreateTheme, 10) !== 1,
);

export const shouldShowThemesUpsell = createSelector(
    getUserTrackingData,
    (trackingData) => parseInt(trackingData.showThemesUpsell, 10) === 1,
);

/* Contacts Access */
export const canUserAccessContacts = createSelector(
    getUserProperties,
    (userProperties) => userProperties.communitySection === USER_PROPERTY_VALUE_SHOW,
);

/* Reporting Access */
export const canUserAccessReporting = createSelector(
    getUserProperties,
    ({ externalTopLevelReportsDashboard, customPeriscopeReports }) => {
        /*
         * If any of these conditions are met, show the user has access to reporting
         * externalTopLevelreportsDashboard is defined
         * customPeriscopeReports is an array with at least one item
         */
        let hasCustomPeriscopeReports;

        // Wrapping the whole thing in a try/catch in case the JSON.parse fails
        try {
            const customReports = JSON.parse(customPeriscopeReports);
            hasCustomPeriscopeReports = Array.isArray(customReports);
        } catch (e) {
            hasCustomPeriscopeReports = false;
        }

        return !!(externalTopLevelReportsDashboard || hasCustomPeriscopeReports);
    },
);

/* Team Tab Access */
export const getCanUserAccessTeam = createSelector(
    getUserProperties,
    (userProperties) => parseInt(userProperties.dashboardTeamTab, 10) === USER_PROPERTY_VALUE_ON,
);

/* Integrations Access */
export const canUserAccessIntegrations = createSelector(
    getUserProperties,
    (userProperties) => userProperties.integrationsAccess === USER_PROPERTY_VALUE_SHOW,
);

/* Use integrations service instead of API */
export const getCanUserSeeIpaas = createSelector(
    getUserProperties,
    (userProperties) => parseInt(userProperties.showiPaasOnOrgIntegrationsPage, 10) === USER_PROPERTY_VALUE_ON,
);

/* Integrations Edit Event Level */
export const canUserEditEventLevelIntegrations = createSelector(
    getUserProperties,
    (userProperties) => userProperties.integrationsEventAccess === USER_PROPERTY_VALUE_SHOW,
);

/**
 * Create Event Level Integration
 */
export const canUserCreateEventLevelIntegration = (state) => {
    const userProperties = getUserProperties(state);
    return (
        parseInt(userProperties.shouldHideCreateEventLevelIntegration, 10) !== USER_PROPERTY_VALUE_ON &&
        userProperties.integrationsAccess === USER_PROPERTY_VALUE_SHOW &&
        userProperties.integrationsEventAccess === USER_PROPERTY_VALUE_SHOW
    );
};

/* Event Overview Access */
export const canUserAccessEventOverview = createSelector(
    getUserProperties,
    (userProperties) => parseInt(userProperties.eventOverview, 10) !== 0, // On by default
);

/* Can Go To Event */
export const getCanUserGoToEvent = createSelector(
    getUserProperties,
    (userProperties) => userProperties.dashboardGoToEvent === USER_PROPERTY_VALUE_SHOW,
);

/* Event Page Access */
export const getCanUserEditEventPage = createSelector(
    getUserProperties,
    (userProperties) => userProperties.finetuneHovers !== USER_PROPERTY_VALUE_HIDE, // Show by default
);

/* Event Settings Access */
export const canUserAccessEventSettings = createSelector(
    getUserProperties,
    ({ dashboardEventSettings }) => dashboardEventSettings === USER_PROPERTY_VALUE_SHOW,
);

/* Guest App Designer Access */
export const canUserAccessGuestAppDesigner = createSelector(
    getUserProperties,
    (userProperties) => parseInt(userProperties.splashGuestAppDesigner, 10) === 1,
);

export const canUserAccessCmsDesignTab = createSelector(
    getUserProperties,
    (userProperties) => userProperties.splashDesignTab === USER_PROPERTY_VALUE_SHOW,
);

export const canUserAccessCmsLayoutTab = createSelector(
    getUserProperties,
    (userProperties) => userProperties.splashLayoutTab === USER_PROPERTY_VALUE_SHOW,
);

/* Events Import Access */
export const canUserAccessEventsImport = createSelector(
    getUserProperties,
    (userProperties) => userProperties.dashboardImportEvents === USER_PROPERTY_VALUE_SHOW,
);

/* Event Duplicate Access */
export const canUserDuplicateEvents = createSelector(
    getUserProperties,
    ({ dashboardDuplicateEvent }) => dashboardDuplicateEvent !== USER_PROPERTY_VALUE_HIDE,
);

/* Event Delete Access */
export const canUserDeleteEvents = createSelector(
    getUserProperties,
    ({ dashboardDelete }) => dashboardDelete !== USER_PROPERTY_VALUE_HIDE,
);

/* Events Create Access */
export const canUserAccessEventsCreate = createSelector(
    getUserProperties,
    (userProperties) => userProperties.dashboardCreate === USER_PROPERTY_VALUE_SHOW,
);

export const canUserAccessGiphyAndUnsplash = createSelector(
    getUserProperties,
    (userProperties) => parseInt(userProperties.hideGiphyAndUnsplash, 10) !== 1,
);

/* Groups section of event collaborators */
export const canUserAccessGroupCollaborators = createSelector(
    getUserProperties,
    (userProperties) => parseInt(userProperties.settingsModalGroupsOnCollaboratorsTab, 10) === 1,
);

export const getCanUserAccessWhiteLabelOptions = createSelector(
    getUserProperties,
    ({ settingsModalWhiteLabel }) => parseInt(settingsModalWhiteLabel, 10) === 1,
);

export const getCanUserAccessEmailWhiteLabelOptions = createSelector(
    getUserProperties,
    ({ settingsModalEmailWhiteLabelDropdown }) => parseInt(settingsModalEmailWhiteLabelDropdown, 10) === 1,
);

/**
 * If the org is restricted to only allow a single registration method
 * @param {Object} state
 * @return {string|undefined}
 */
export const getRestrictedToRegistrationMethod = createSelector(
    getUserProperties,
    ({ restrictedRsvpMethod }) => restrictedRsvpMethod,
);

/**
 * If the user can change publish/unpublish events value
 */
export const canUserModifyPublished = createSelector(
    getUserProperties,
    ({ publishEventsDisablePermissions }) => parseInt(publishEventsDisablePermissions, 10) === 0,
);

/**
 * The user can see approval flows on events
 */
export const canAccessEventApprovalFlow = createSelector(
    getUserProperties,
    ({ eventApprovalFlow }) => parseInt(eventApprovalFlow, 10) === 1,
);

/**
 * The user can add events to hubs while approving events
 */
export const canAddToHubAfterApproveEvent = createSelector(
    getUserProperties,
    ({ eventApprovalFlowAddToHubApproving }) => parseInt(eventApprovalFlowAddToHubApproving, 10) === 1,
);

/*
 * Access to the Groups tab in Account settings and to Groups tab in Team Dashboard
 * Corresponds to the "Manage Groups to All Users" org property
 */
export const canUserAccessGroupSettings = createSelector(
    getUserProperties,
    (userProperties) => userProperties.manageGroups === USER_PROPERTY_VALUE_SHOW,
);
/* Forces hiding Group settings, even if the user is a group manager */
export const getShouldHideGroupSettings = createSelector(
    getUserProperties,
    ({ forceHideManageGroups }) => parseInt(forceHideManageGroups, 10) === 1,
);

/*
 * Can user see and edit custom event field questions in event settings and event duplication
 * Org prop: Create Flow (form only): Create Flow Survey ID
 */
export const getCanUserAccessCustomEventFields = createSelector(
    getUserProperties,
    (userProperties) => userProperties.allowEditCreateFlowSurveyQuestions === '1',
);

/**
 * For some organizations, through an org property, we customize the in-product copy
 * around adding events to hubs. Often changed when organizations use hubs as an approval flow.
 */
export const getAddToHubText = createSelector(getUserProperties, ({ dashboardAddToHubText }) =>
    typeof dashboardAddToHubText === 'string' ? dashboardAddToHubText : '',
);

/* hide domain field in event create form */
export const hideDomainInCreateFlow = createSelector(
    getUserProperties,
    (userProperties) => parseInt(userProperties.hideDomainInCreateFlow, 10) === 1,
);

/**
 * For some organizations, through an org property, we customize the in-product copy
 * around deleting events. Often changed when organizations use deleting as an approval flow.
 */
export const getEventDeleteText = createSelector(getUserProperties, ({ dashboardDeleteText }) =>
    typeof dashboardDeleteText === 'string' ? dashboardDeleteText : '',
);

/* This changes the name for 'hubs' everywhere in the UI */
export const getGlobalHubName = createSelector(getUserProperties, ({ hubText }) =>
    typeof hubText === 'string' ? hubText : '',
);

/**
 * This gets the an override org property for the value listed in the Event Dashboard filter group.
 * This replaces the label of the negative option.
 */
export const getFilterLabelNotListedInHub = createSelector(getUserProperties, ({ labelUnlistedEventsFilter }) =>
    typeof labelUnlistedEventsFilter === 'string' ? labelUnlistedEventsFilter : '',
);

/**
 * This gets the an override org property for the value listed in the Event Dashboard filter group.
 * This replaces the label of the positive option.
 */
export const getFilterLabelListedInHub = createSelector(getUserProperties, ({ labelListedEventsOnHubsFilter }) =>
    typeof labelListedEventsOnHubsFilter === 'string' ? labelListedEventsOnHubsFilter : '',
);

/**
 * Get the value of the org property for setting a custom create flow.
 * This value replaces instances of `/events/create`
 */
export const getCustomCreateFlowUrl = createSelector(
    getUserProperties,
    ({ customCreateEventFlowUrl }) => customCreateEventFlowUrl,
);

/* Can users add events to hubs from the event dashboard */
export const canUserAddToHub = createSelector(
    getUserProperties,
    ({ dashboardAddToHub }) => dashboardAddToHub !== USER_PROPERTY_VALUE_HIDE,
);

/* Adds option to to Page Privacy in event settings to include SSO */
export const canUserSetSSOPagePrivacy = createSelector(
    getUserProperties,
    ({ attendeeSSOPagePrivacySetting }) => parseInt(attendeeSSOPagePrivacySetting, 10) === 1,
);

/* Can the user create/set/modify an event's vanity url */
export const getCanUserSetVanityUrl = createSelector(
    getUserProperties,
    ({ settingsModalVanityUrlVisibility }) => settingsModalVanityUrlVisibility === USER_PROPERTY_VALUE_SHOW,
);

/* Can the user access the event tags in event settings */
export const getCanUserAccessEventTags = createSelector(
    getUserProperties,
    ({ settingsModalTags }) => settingsModalTags === USER_PROPERTY_VALUE_SHOW,
);

export const getCanUserChangeDomainName = createSelector(
    getUserProperties,
    ({ changeDomainName }) => changeDomainName === USER_PROPERTY_VALUE_SHOW,
);

/* Can user see the Event type drop down */
export const getCanUserAccessEventType = createSelector(
    getUserProperties,
    (userProperties) => userProperties.createChangeType === USER_PROPERTY_VALUE_SHOW,
);

/* Can the user access post event surveys */
export const canUserAccessPostEventSurveys = createSelector(
    getUserProperties,
    (userProperties) => userProperties.postEventSurveys === USER_PROPERTY_VALUE_SHOW,
);

export const canUserAccessTypeaheadInCollaboratorsView = createSelector(
    getUserProperties,
    (userProperties) => parseInt(userProperties.disableCollaboratorsTypeaheadSearch, 10) === USER_PROPERTY_VALUE_ON,
);

export const getFormTabHideAddQuestionButton = createSelector(
    getUserProperties,
    (userProperties) => parseInt(get(userProperties, 'formTabHideAddQuestionButton', 0), 10) === USER_PROPERTY_VALUE_ON,
);

/*
 * Create Flow: Add Single Group Selection
 *
 * Shows a dropdown for a single group in event create/event duplicate. This group is automatically
 * added as a group collaborator for the event.
 */
export const getCanUserAccessCreateFlowSingleGroupSelection = createSelector(
    getUserProperties,
    (userProperties) => parseInt(userProperties.createFlowSingleGroupSelection, 10) === USER_PROPERTY_VALUE_ON,
);

/*
 * Create Flow: Add Single Group Selection Label
 *
 * A label to be used for the single group dropdown in the create flow
 */
export const getCreateFlowSingleGroupSelectionLabel = createSelector(
    getUserProperties,
    (userProperties) => userProperties.createFlowSingleGroupSelectionLabel || '',
);

/*
 * Create Flow: Add Single Group Selection Specific Group Ids
 *
 * A list of comma separated group ids. To be used in the single group dropdown in the create flow.
 * This list limits the dropdown to only contain the specified groups.
 */
export const getCreateFlowSingleGroupIdLimits = createSelector(getUserProperties, (userProperties) =>
    userProperties.createFlowSingleGroupSelectionSpecificGroupIds
        ? userProperties.createFlowSingleGroupSelectionSpecificGroupIds.split(',')
        : [],
);

/*
 * Create Flow: Add Single Group Required
 *
 * Determines if the single group field should be a required input in the create flow
 */
export const getIsCreateFlowSingleGroupSelectionRequired = createSelector(
    getUserProperties,
    getCanUserAccessCreateFlowSingleGroupSelection,
    (userProperties, canUserAccessCreateFlowSingleGroupSelection) =>
        canUserAccessCreateFlowSingleGroupSelection &&
        parseInt(userProperties.createFlowSingleGroupSelectionRequired, 10) === USER_PROPERTY_VALUE_ON,
);

/*
 * Can the user work with the legacy v2 forms
 */
export const getAllowV2Form = createSelector(
    getUserProperties,
    (userProperties) => parseInt(get(userProperties, 'allowV2Form', 0), 10) === USER_PROPERTY_VALUE_ON,
);

/**
 * Should user permissions source from the role based system or user properties
 */
export const getIsUserUsingRoleBasedPermissions = createSelector(
    getUserProperties,
    (userProperties) => parseInt(get(userProperties, 'useRoleBasedPermissions', 0), 10) === USER_PROPERTY_VALUE_ON,
);

/**
 * Access to the touchpoint for virtual event building
 */
export const getCanUserAccessVirtualEventTouchpoint = createSelector(
    getUserProperties,
    (userProperties) => parseInt(get(userProperties, 'virtualEventTouchpoint', 0), 10) === USER_PROPERTY_VALUE_ON, // eslint-disable-line max-len
);

/**
 * Access to the touchpoint for virtual event building
 */
export const getIsUserVirtualViewOnly = createSelector(
    getUserProperties,
    (userProperties) =>
        parseInt(get(userProperties, 'virtualEventViewerPermissions', 0), 10) === USER_PROPERTY_VALUE_ON, // eslint-disable-line max-len
);

/**
 * Can change the locale of a event
 */
export const getCanUserChangeEventLocale = createSelector(
    getUserProperties,
    (userProperties) => parseInt(get(userProperties, 'enforceLocaleEventSelection', 0), 10) === USER_PROPERTY_VALUE_ON, // eslint-disable-line max-len
);

/* Guest List Access */
export const getCanUserValidateIpAddress = createSelector(
    getUserProperties,
    (userProperties) => parseInt(get(userProperties, 'validateIpAddressForLogin', 0), 10) === USER_PROPERTY_VALUE_ON, // eslint-disable-line max-len
);

/* Form Library Restricted Access */
export const getFormLibraryRestrictedAccess = createSelector(
    getUserProperties,
    (userProperties) => parseInt(get(userProperties, 'formLibraryRestrictedAccess', 0), 10) === USER_PROPERTY_VALUE_ON, // eslint-disable-line max-len
);

/**
 * Can the user access the Org Level Form Library
 */
export const getFormLibraryManager = createSelector(
    getUserProperties,
    (userProperties) => parseInt(get(userProperties, 'formLibraryManager', 0), 10) === USER_PROPERTY_VALUE_ON,
);

/**
 * Can the user access the Org Level Block Library
 */
export const getCanUserAccessBlockLibrary = createSelector(
    getUserProperties,
    (userProperties) => parseInt(get(userProperties, 'blockLibraryManager', 0), 10) === USER_PROPERTY_VALUE_ON,
);

/* Locale support in form library */
export const canUserUseFormLibraryLocales = createSelector(
    getUserProperties,
    (userProperties) => parseInt(get(userProperties, 'formLibraryLocaleSupport', 0), 10) === USER_PROPERTY_VALUE_ON, // eslint-disable-line max-len
);

/* Privacy Settings Access */
export const getCanUserAccessPrivacySettings = createSelector(
    getUserProperties,
    (userProperties) => userProperties.settingsModalPrivacy === USER_PROPERTY_VALUE_SHOW,
);

/**
 * Can the user access the Org Level Dynamic Localized Tags Library
 */
export const getCanUserAccessDynamicLocalizedTagsManager = createSelector(
    getUserProperties,
    (userProperties) => parseInt(get(userProperties, 'dynamicLocalizedTagsManager', 0), 10) === USER_PROPERTY_VALUE_ON, // eslint-disable-line max-len
);
/* Self Serve Global Brand Manager */
export const getCanUserAccessGlobalBrandManager = createSelector(
    getUserProperties,
    (userProperties) => parseInt(get(userProperties, 'selfServeGlobalBrandManager', 0), 10) === USER_PROPERTY_VALUE_ON, // eslint-disable-line max-len
);

/**
 * Checks if user's org has LicenseCountAndWarnings enabled
 */
export const getEnableLicenseCountAndWarnings = createSelector(
    getUserProperties,
    (userProperties) => !!parseInt(userProperties.enableLicenseCountAndWarnings, 10),
);

/**
 * Get the custom ticket fee structure
 */
export const getTicketRates = createSelector(getUserProperties, (userProperties) =>
    get(userProperties, 'ticketRates', TICKET_RATES_2016),
);

/**
 * Checks if the org has data anon settings enabled
 */
export const getIsDataAnonymizationAdvancedSettingsEnabled = createSelector(
    getUserProperties,
    (userProperties) => !!parseInt(userProperties.dataAnonymizationAdvancedSettings, 10),
);
/**
 * Checks if the org has artificial intelligence features enabled
 */
export const getHasOrgDisabledArtificialIntelligenceFeatures = createSelector(
    getUserProperties,
    (userProperties) => parseInt(userProperties.disableArtificialIntelligenceFeatures, 10) === USER_PROPERTY_VALUE_ON,
);

export const getIsPrivacyManagementEnabled = createSelector(
    getUserProperties,
    (userProperties) => parseInt(userProperties.privacyManagement, 10) === USER_PROPERTY_VALUE_ON,
);

export const getIsAiSiteDesignerEnabled = createSelector(
    getUserProperties,
    (userProperties) => parseInt(userProperties.aiSiteDesigner, 10) === USER_PROPERTY_VALUE_ON,
);

export const canUserAccessPayoutsTab = createSelector(
    getUserProperties,
    (userProperties) => parseInt(userProperties.hidePaymentOptions, 10) === USER_PROPERTY_VALUE_OFF,
);

export const shouldShowSalesforceBidirectionalSyncButton = createSelector(
    getUserProperties,
    (userProperties) => userProperties.salesforceBidirectionalSyncButton === USER_PROPERTY_VALUE_SHOW,
);

export const getSalesforceAppV3IntegrationEnabled = createSelector(
    getUserProperties,
    (userProperties) => parseInt(userProperties.salesforceAppV3Integration, 10) === USER_PROPERTY_VALUE_ON,
);

export const getSalesforceIntegrationViaAppExchange = createSelector(
    getUserProperties,
    (userProperties) => parseInt(userProperties.salesforceIntegrationViaAppExchange, 10) === USER_PROPERTY_VALUE_ON,
);

export const getIsShareGuestListEnabled = createSelector(
    getUserProperties,
    (userProperties) => parseInt(userProperties.enableShareGuestLists, 10) === USER_PROPERTY_VALUE_ON,
);

/**
 * Check if attendance type restriction is set
 */
export const getIsAttendanceTypeRestricted = createSelector(
    getUserProperties,
    (userProperties) => !!userProperties.attendanceTypeRestriction,
);

export const getIsChangeReportEnabled = createSelector(
    getUserProperties,
    (userProperties) => parseInt(userProperties.enableChangeReport, 10) === USER_PROPERTY_VALUE_ON,
);

export const getIsEventMultipleRegistrationFormsEnabled = createSelector(
    getUserProperties,
    getIsEventDefaultFormV2,
    (userProperties, isDefaultFormV2) =>
        !isDefaultFormV2 &&
        parseInt(userProperties.enableEventMultipleRegistrationForms, 10) === USER_PROPERTY_VALUE_ON,
);

export const getIsMobileWebCheckinDisabled = createSelector(
    getUserProperties,
    (userProperties) => parseInt(userProperties.disableMobileWebCheckin, 10) === USER_PROPERTY_VALUE_ON,
);
